import { Fragment, useDeferredValue, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { Card, CardHeader, InlineStat } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Nil, Table, THead, Th, Td, Tr } from '../../components/ui/Table';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { Pagination } from '../../components/ui/Pagination';
import { SyncedAt } from '../../components/ui/InfoTip';
import { useAccess } from '../../hooks/useAccess';
import { useCsmAosOverview, useCsmAosCompanyDetail, useCsmRoster } from '../../hooks/useDashboard';
import { bareDomain } from '../Sales/shared';
import type { CsmAosBreakdownRow, CsmAosCompanyRow, CsmAosSeriesPoint } from '../../api/dashboard';
import { formatDayMonth, formatDayMonthTime } from '../../utils/dateFormat';
import { formatCompact, formatNumber } from '../../utils/numberFormat';
import {
  CHART_AXIS,
  CHART_GRID,
  CHART_HEIGHT,
  CHART_SERIES,
  CHART_TOOLTIP,
  ChartEmpty,
} from '../../components/ui/chart';
import { shiftDaysISO, todayISO, withoutPartialToday } from './csm/period';
import { AttioCompanyLink } from './csm/attio';
import { RosterBridge } from './csm/RosterBridge';
import { UsageOverview } from './csm/UsageOverview';

// CSM › Usage › aOS platform — token consumption from the new platform's
// ai_usage ledger (Nova + bayard), mirrored into synced_ai_usage. Whole-group
// overview + drill into one company. The token composition and the model /
// provider / operation breakdowns are an engineering cost view, so admin-only.

const PAGE_SIZE = 15;

/** How the API turns tokens into credits, the same rule as the platform's usage dashboard. */
const CREDITS_RULE = '1 credit = 1,000 tokens, cached input at 25%';

/** "71.5M" with the full figure on hover. */
function Compact({ value }: { value: number }) {
  return <span title={formatNumber(value)}>{formatCompact(value) || '0'}</span>;
}

/**
 * The Classic tab's KPI pill, from this window against the one before. No pill
 * when the previous window is unknown (still loading, failed) or was empty:
 * a change from zero says nothing.
 */
function deltaChange(current: number, previous: number | undefined): { value: string; trend: 'up' | 'down' | 'neutral' } | undefined {
  if (previous === undefined || previous <= 0) return undefined;
  const pct = ((current - previous) / previous) * 100;
  const rounded = Math.round(pct);
  const trend = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'neutral';
  if (pct >= 900) return { value: '>10× vs previous period', trend };
  return { value: `${rounded > 0 ? '+' : rounded < 0 ? '−' : ''}${Math.abs(rounded)}% vs previous period`, trend };
}

function formatTokenDay(value: string): string {
  return formatDayMonth(`${value}T00:00:00Z`);
}

function tokenChart(series: CsmAosSeriesPoint[]) {
  const color = CHART_SERIES[0];
  return (
    <AreaChart data={withoutPartialToday(series)} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
      <CartesianGrid {...CHART_GRID} />
      <XAxis dataKey="date" tickFormatter={formatTokenDay} {...CHART_AXIS} minTickGap={32} />
      <YAxis tickFormatter={(v: number) => formatCompact(v)} {...CHART_AXIS} width={52} tickCount={4} />
      <Tooltip
        {...CHART_TOOLTIP}
        formatter={(value) => [formatNumber(Number(value ?? 0)), 'Tokens'] as [string, string]}
        labelFormatter={(label) => formatTokenDay(String(label))}
      />
      <Area
        type="linear"
        dataKey="totalTokens"
        stroke={color}
        strokeWidth={2}
        fill={color}
        fillOpacity={0.12}
        dot={false}
        isAnimationActive={false}
      />
    </AreaChart>
  );
}

const PROVIDER_LABELS: Record<string, string> = {
  'openai.responses': 'OpenAI, responses',
  'openai.embedding': 'OpenAI, embeddings',
  'openai.chat': 'OpenAI, chat',
};

/**
 * A breakdown key as words: "chunk-embedding" → "Chunk embedding",
 * "openai.responses" → "OpenAI, responses". Model names stay as they are
 * ("gpt-5.6-luna" is a name). The raw key is on hover.
 */
function breakdownLabel(title: string, key: string): string {
  if (title === 'By model') return key;
  if (PROVIDER_LABELS[key]) return PROVIDER_LABELS[key];
  const words = key.replace(/[._-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

function BreakdownList({ title, rows }: { title: string; rows: CsmAosBreakdownRow[] }) {
  const max = rows.reduce((acc, row) => Math.max(acc, row.totalTokens), 0);
  return (
    <Card padding="sm">
      <CardHeader title={title} />
      {rows.length === 0 ? (
        <EmptyState title="No data in this period" compact />
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => (
            <li key={row.key}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="break-words text-body-sm text-text-primary" title={row.key}>
                  {breakdownLabel(title, row.key)}
                </span>
                <span className="shrink-0 text-body-sm tabular-nums text-text-secondary">
                  <Compact value={row.totalTokens} />
                </span>
              </div>
              <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-surface-tertiary">
                <div
                  className="h-full rounded-full"
                  style={{
                    backgroundColor: CHART_SERIES[0],
                    width: max > 0 ? `${Math.max(2, (row.totalTokens / max) * 100)}%` : '0%',
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * The admin breakdown cards, minus the empty ones: an empty card per dimension
 * reads as broken. When every list is empty, one compact line says so.
 */
function Breakdowns({ lists, composition }: { lists: { title: string; rows: CsmAosBreakdownRow[] }[]; composition?: ReactNode }) {
  // The longest list stands alone on the left; the composition card and the shorter lists stack on the right,
  // so a short list never leaves a hole beside a long one.
  const filled = lists.filter((l) => l.rows.length > 0).sort((a, b) => b.rows.length - a.rows.length);
  const [longest, ...rest] = filled;
  if (!longest) {
    return (
      <div className="space-y-4">
        {composition}
        <p className="text-body-xs text-text-tertiary">
          No model, provider or operation breakdown recorded in this period.
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
      <BreakdownList title={longest.title} rows={longest.rows} />
      <div className="space-y-4">
        {composition}
        {rest.map((l) => (
          <BreakdownList key={l.title} title={l.title} rows={l.rows} />
        ))}
      </div>
    </div>
  );
}

function UsageTable({
  label,
  rows,
}: {
  label: string;
  rows: Array<{ key: string; name: string; sub?: string | null; total: number; input: number; output: number; calls: number }>;
}) {
  return (
    <div className="space-y-2">
      <h4 className="text-body-sm font-medium text-text-primary">By {label.toLowerCase()}</h4>
      {rows.length === 0 ? (
        <p className="text-body-sm text-text-tertiary">No {label.toLowerCase()}-attributed usage.</p>
      ) : (
        <Table>
          <THead>
            <tr className="border-b border-border-light">
              <Th>{label}</Th>
              <Th align="right">Total</Th>
              <Th align="right">Input</Th>
              <Th align="right">Output</Th>
              <Th align="right">Calls</Th>
            </tr>
          </THead>
          <tbody>
            {rows.map((row) => (
              <Tr key={row.key}>
                <Td>
                  <p className="text-text-primary">{row.name}</p>
                  {row.sub && <p className="text-body-xs text-text-tertiary">{row.sub}</p>}
                </Td>
                <Td align="right" className="tabular-nums text-text-secondary">
                  <Compact value={row.total} />
                </Td>
                <Td align="right" className="tabular-nums text-text-tertiary">
                  <Compact value={row.input} />
                </Td>
                <Td align="right" className="tabular-nums text-text-tertiary">
                  <Compact value={row.output} />
                </Td>
                <Td align="right" className="tabular-nums text-text-tertiary">
                  {formatNumber(row.calls)}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

function CompanyDetail({ companyId, window }: { companyId: string; window: { from: string; to: string } }) {
  const { isAdmin } = useAccess();
  const { data, isLoading, error, refetch } = useCsmAosCompanyDetail(companyId, window);

  if (isLoading) return <LoadingState />;
  if (error || !data) {
    return <ErrorState title="Couldn’t load this company’s token breakdown" error={error} onRetry={() => refetch()} />;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <InlineStat label="Tokens" value={formatCompact(data.company.totalTokens) || '0'} size="sm" />
        <InlineStat label="Credits" value={formatNumber(data.company.credits)} sub={CREDITS_RULE} size="sm" />
        <InlineStat label="Calls" value={formatNumber(data.company.calls)} size="sm" />
        <InlineStat label="Active users" value={formatNumber(data.company.activeUsers)} size="sm" />
      </div>

      {(data.staffTokens ?? 0) > 0 && (
        <p className="text-body-xs text-text-tertiary">
          SEQUESTO staff used another <Compact value={data.staffTokens ?? 0} /> tokens in this workspace, left out of
          every figure here, as on Customers.
        </p>
      )}

      {/* The row is already inside the companies card: its parts are sections, not cards. */}
      <div className="space-y-2">
        <h4 className="text-body-sm font-medium text-text-primary">Tokens over time</h4>
        {data.series.every((p) => p.totalTokens === 0) ? (
          <ChartEmpty label="No token activity in this period." height={CHART_HEIGHT.small} />
        ) : (
          <ResponsiveContainer width="100%" height={CHART_HEIGHT.small}>
            {tokenChart(data.series)}
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 border-t border-border-light pt-4 lg:grid-cols-2">
        <UsageTable
          label="User"
          rows={data.byUser.map((r) => ({
            key: r.userExternalId ?? 'unattributed',
            name: r.name,
            sub: r.email,
            total: r.totalTokens,
            input: r.inputTokens,
            output: r.outputTokens,
            calls: r.calls,
          }))}
        />
        <UsageTable
          label="Project"
          rows={data.byProject.map((r) => ({
            key: r.projectExternalId ?? 'unattributed',
            name: r.projectName ?? (r.projectExternalId ? r.projectExternalId.slice(0, 8) : 'Unattributed'),
            total: r.totalTokens,
            input: r.inputTokens,
            output: r.outputTokens,
            calls: r.calls,
          }))}
        />
      </div>

      {isAdmin && (
        <Breakdowns
          lists={[
            { title: 'By model', rows: data.byModel },
            { title: 'By operation type', rows: data.byOperationType },
          ]}
        />
      )}
    </div>
  );
}

export function CsmAosDashboard({ days }: { days: number }) {
  const { isAdmin } = useAccess();
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [expandedCompanyId, setExpandedCompanyId] = useState<string | null>(null);

  const search = useDeferredValue(searchInput);
  useEffect(() => setPage(1), [days]);
  const roster = useCsmRoster(true);
  const rosterDomainByRecord = useMemo(
    () => new Map((roster.data?.rows ?? []).filter((r) => r.domain).map((r) => [r.attioRecordId, r.domain as string])),
    [roster.data],
  );
  const window = useMemo(() => ({ from: shiftDaysISO(days), to: todayISO() }), [days]);
  // The same-length window just before, for the KPI trend pills (same pills as Classic).
  const previous = useCsmAosOverview({ from: shiftDaysISO(2 * days), to: shiftDaysISO(days + 1), search: '', page: 1, pageSize: 1 });
  // The previous window only counts when the ledger mirror reaches back over it;
  // otherwise "vs previous period" compares usage with missing data.
  const coverageStart = previous.data?.summary.coverageStart ?? null;
  const previousCovered = !!coverageStart && coverageStart <= shiftDaysISO(2 * days);
  const prev = previousCovered ? previous.data?.summary : undefined;

  const { data, isLoading, error, refetch, isFetching } = useCsmAosOverview({
    from: window.from,
    to: window.to,
    search,
    page,
    pageSize: PAGE_SIZE,
  });

  if (error) return <ErrorState title="Couldn’t load aOS token usage" error={error} onRetry={() => refetch()} />;
  if (isLoading && !data) return <LoadingState rows={4} />;
  if (!data) return null;

  const summary = data.summary;
  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      {data.lastSyncAt && (
        <SyncedAt>
          Synced {formatDayMonthTime(data.lastSyncAt)}
          {isFetching && ' · refreshing…'}
        </SyncedAt>
      )}

      <UsageOverview
        activeUsers={{
          value: formatNumber(summary.activeUsers),
          change: deltaChange(summary.activeUsers, prev?.activeUsers),
          sub: `Last ${days} days · customer users with an AI call`,
          tooltip: 'Customer users who made at least one AI call in the period. Calls with no user attached are not counted. SEQUESTO staff are left out.',
        }}
        tokens={{
          value: formatCompact(summary.totalTokens) || '0',
          tooltip: `${formatNumber(summary.totalTokens)} tokens by customer users. SEQUESTO staff in customer workspaces are left out, as on Customers.`,
          change: deltaChange(summary.totalTokens, prev?.totalTokens),
          sub: previous.error
            ? 'Previous period unavailable, so no trend'
            : previous.data && !previousCovered
              ? `No trend: aOS data starts ${coverageStart ? formatTokenDay(coverageStart) : 'later'}`
              : (summary.excludedStaffTokens ?? 0) > 0
                ? `Customer users · ${formatCompact(summary.excludedStaffTokens ?? 0)} by SEQUESTO staff left out`
                : `Last ${days} days · customer users`,
        }}
        credits={{
          value: formatNumber(summary.credits),
          tooltip: `Credits as the platform's usage dashboard counts them: ${CREDITS_RULE}. Customer users only, like Tokens.`,
          change: deltaChange(summary.credits, prev?.credits),
          sub: CREDITS_RULE,
        }}
        activeCustomers={{
          value: formatNumber(summary.activeCompanies),
          change: deltaChange(summary.activeCompanies, prev?.activeCompanies),
          sub:
            data.bridge && data.bridge.onRosterCompanies <= summary.activeCompanies
              ? `${formatNumber(data.bridge.onRosterCompanies)} on the CSM list + ${formatNumber(summary.activeCompanies - data.bridge.onRosterCompanies)} other workspaces`
              : "A customer = a workspace with AI usage · SEQUESTO's own left out",
          tooltip:
            "A customer is a workspace with aOS AI usage in the period, on the CSM list or not (trials, prospects, personal workspaces). SEQUESTO's own workspaces are left out. Usage › Classic counts live roster customers with a login instead.",
        }}
        bridge={
          data.bridge && (
            <RosterBridge
              platform="aOS"
              total={summary.totalTokens}
              bridge={data.bridge}
              days={days}
              note="Customer users only: SEQUESTO staff are left out on both sides."
            />
          )
        }
        series={data.series.map((p) => ({
          date: p.date,
          tokens: p.totalTokens,
          activeUsers: p.activeUsers,
          activeCompanies: p.activeCompanies,
        }))}
      />

      {isAdmin && (
        <>
          <Breakdowns
            composition={
          <Card padding="sm">
            <CardHeader title="Token composition" description="Admin only" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <InlineStat label="Calls" value={formatNumber(summary.totalCalls)} size="sm" />
              <InlineStat label="Input" value={formatCompact(summary.inputTokens) || '0'} sub="includes cached" size="sm" />
              <InlineStat label="Output" value={formatCompact(summary.outputTokens) || '0'} sub="includes reasoning" size="sm" />
              <InlineStat label="Cached input" value={formatCompact(summary.cachedInputTokens) || '0'} size="sm" />
              <InlineStat label="Reasoning" value={formatCompact(summary.reasoningTokens) || '0'} size="sm" />
            </div>
          </Card>
            }
            lists={[
              { title: 'By model', rows: data.byModel },
              { title: 'By provider', rows: data.byProvider },
              { title: 'By operation type', rows: data.byOperationType },
            ]}
          />
        </>
      )}

      <Card padding="sm">
        <CardHeader
          title="Companies by consumption"
          description={`${formatNumber(data.total)} with usage`}
          action={
            <div className="w-56">
              <Input
                placeholder="Search company"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-1.5 text-body-xs"
              />
            </div>
          }
        />
        {data.companies.length === 0 ? (
          <EmptyState title="No companies with token usage in this period" compact />
        ) : (
          <Table minWidth={640}>
            <THead>
              <tr className="border-b border-border-light">
                <Th className="w-6" />
                <Th>Company</Th>
                <Th align="right">Tokens</Th>
                <Th align="right">Credits</Th>
                <Th align="right">Calls</Th>
                <Th align="right">Users</Th>
                <Th align="right">Last activity</Th>
                <Th className="w-10 !pr-0">
                  <span className="sr-only">Attio</span>
                </Th>
              </tr>
            </THead>
            <tbody>
              {data.companies.map((company: CsmAosCompanyRow) => {
                const expanded = expandedCompanyId === company.id;
                return (
                  <Fragment key={company.id}>
                    <Tr onClick={() => setExpandedCompanyId(expanded ? null : company.id)}>
                      <Td className="pr-2">
                        <ChevronRight
                          className={clsx('h-4 w-4 text-text-muted transition-transform', expanded && 'rotate-90')}
                          aria-hidden
                        />
                      </Td>
                      <Td>
                        {/* Attio name first; the workspace name or domain as the grey line when it differs. */}
                        <p className="flex items-center gap-2 text-text-primary">
                          {company.attioName ?? company.name}
                          {company.onRoster === false && (
                            <span className="text-body-xs text-text-tertiary" title="Not a live customer on the CSM roster, so Customers doesn't count it">
                              not on the roster
                            </span>
                          )}
                        </p>
                        {/* One domain rule: the CSM list's domain when the company is on it, else the platform's;
                            the workspace name only when it differs from the Attio name ("ISS" under ISS UK). */}
                        {(() => {
                          const workspace =
                            company.attioName && company.attioName.toLowerCase() !== company.name.toLowerCase() ? company.name : null;
                          const domain = bareDomain(
                            (company.attioRecordId ? rosterDomainByRecord.get(company.attioRecordId) : null) ?? company.domain,
                          );
                          const line = [workspace && `workspace ${workspace}`, domain].filter(Boolean).join(' · ');
                          return line ? <p className="text-body-xs text-text-tertiary">{line}</p> : null;
                        })()}
                      </Td>
                      <Td align="right" className="tabular-nums text-text-secondary">
                        <Compact value={company.totalTokens} />
                      </Td>
                      <Td align="right" className="tabular-nums text-text-secondary">
                        {formatNumber(company.credits)}
                      </Td>
                      <Td align="right" className="tabular-nums text-text-tertiary">
                        {formatNumber(company.calls)}
                      </Td>
                      <Td align="right" className="tabular-nums text-text-tertiary">
                        {formatNumber(company.activeUsers)}
                      </Td>
                      <Td align="right" className="whitespace-nowrap text-text-tertiary">
                        {company.lastEventAt ? formatDayMonthTime(company.lastEventAt) : <Nil />}
                      </Td>
                      <Td align="right" className="!pr-0">
                        {company.attioRecordId ? (
                          <AttioCompanyLink recordId={company.attioRecordId} name={company.attioName} />
                        ) : null}
                      </Td>
                    </Tr>
                    {expanded && (
                      <tr className="border-b border-border-light bg-surface-secondary/40">
                        <td colSpan={8} className="p-4">
                          <CompanyDetail companyId={company.id} window={window} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        )}
        <Pagination page={page} pageCount={totalPages} total={data.total} pageSize={PAGE_SIZE} onPage={setPage} />
      </Card>
    </div>
  );
}
